import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { getDSInputs, saveDSInputs, DSInputRow } from '../services/api';
import { BUSINESSES, DEFAULT_BUSINESS } from '../appConfig';

interface EditableRow {
    location_id: string;
    cycle_name: string;
    utr: string;
}

const EMPTY_ROW: EditableRow = { location_id: '', cycle_name: '', utr: '' };

/** Delivery date options: today + the next 7 days, formatted YYYY-MM-DD. */
function ofdDateOptions(): string[] {
    const days: string[] = [];
    const base = new Date();
    for (let i = 0; i < 8; i++) {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        days.push(d.toISOString().slice(0, 10));
    }
    return days;
}

const DeliveryStationInputs: React.FC = () => {
    const dateOptions = useMemo(() => ofdDateOptions(), []);

    const [business, setBusiness] = useState<string>(DEFAULT_BUSINESS);
    const [ofdDate, setOfdDate] = useState<string>(dateOptions[0]);
    const [rows, setRows] = useState<EditableRow[]>([{ ...EMPTY_ROW }]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const loadInputs = useCallback(async () => {
        if (!business || !ofdDate) return;
        setIsLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const result = await getDSInputs(business, ofdDate);
            const items = Array.isArray(result?.items) ? result.items : [];
            const loaded: EditableRow[] = items.map((it: any) => ({
                location_id: it.location_id ?? '',
                cycle_name: it.cycle_name ?? '',
                utr: it.utr != null ? String(it.utr) : '',
            }));
            setRows(loaded.length > 0 ? loaded : [{ ...EMPTY_ROW }]);
        } catch (err: any) {
            setError(err.message || 'Failed to load inputs');
            setRows([{ ...EMPTY_ROW }]);
        } finally {
            setIsLoading(false);
        }
    }, [business, ofdDate]);

    useEffect(() => {
        loadInputs();
    }, [loadInputs]);

    const updateRow = (idx: number, field: keyof EditableRow, value: string) => {
        setRows(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
    };

    const addRow = () => setRows(prev => [...prev, { ...EMPTY_ROW }]);
    const removeRow = (idx: number) => setRows(prev => prev.filter((_, i) => i !== idx));

    const handleSave = async () => {
        setError(null);
        setSuccess(null);

        const validRows = rows.filter(r => r.location_id.trim() && r.cycle_name.trim());
        if (validRows.length === 0) {
            setError('Add at least one row with a location and cycle.');
            return;
        }

        const items: DSInputRow[] = validRows.map(r => ({
            location_id: r.location_id.trim(),
            cycle_name: r.cycle_name.trim(),
            utr: r.utr.trim() === '' ? 0 : Number(r.utr),
        }));

        setIsSaving(true);
        try {
            const result = await saveDSInputs(business, ofdDate, items);
            setSuccess(`Saved ${result.written} row(s).`);
        } catch (err: any) {
            setError(err.message || 'Failed to save inputs');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Column spacing="400" spacingInset="500">
            <Heading level={2}>Delivery Station Inputs</Heading>
            <Text color="secondary">
                Provide per-station UTR inputs for a business and delivery date. These feed the cap-setting pipeline.
            </Text>

            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            <Row spacing="300" widths={['grid-3', 'grid-3']} alignmentVertical="bottom">
                <Column spacing="100">
                    <Text type="b200">Business</Text>
                    <Select value={business} onChange={setBusiness} placeholder="Business">
                        {BUSINESSES.map(b => (
                            <SelectOption key={b} value={b} label={b.toUpperCase()} />
                        ))}
                    </Select>
                </Column>
                <Column spacing="100">
                    <Text type="b200">Delivery date (OFD)</Text>
                    <Select value={ofdDate} onChange={setOfdDate} placeholder="Delivery date">
                        {dateOptions.map(d => (
                            <SelectOption key={d} value={d} label={d} />
                        ))}
                    </Select>
                </Column>
            </Row>

            {isLoading ? (
                <Row alignmentHorizontal="center" spacingInset="500"><Loader /></Row>
            ) : (
                <Column spacing="300">
                    <Table headerRows={1} showDividers spacing="small">
                        <TableRow>
                            <TableCell>Location ID</TableCell>
                            <TableCell>Cycle</TableCell>
                            <TableCell>UTR</TableCell>
                            <TableCell />
                        </TableRow>
                        {rows.map((row, idx) => (
                            <TableRow key={idx}>
                                <TableCell>
                                    <Input
                                        value={row.location_id}
                                        onChange={(v: string) => updateRow(idx, 'location_id', v)}
                                        placeholder="e.g. TYO3"
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        value={row.cycle_name}
                                        onChange={(v: string) => updateRow(idx, 'cycle_name', v)}
                                        placeholder="e.g. SSD1"
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Input
                                        value={row.utr}
                                        onChange={(v: string) => updateRow(idx, 'utr', v)}
                                        type="number"
                                        placeholder="0"
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        type="tertiary"
                                        size="small"
                                        onClick={() => removeRow(idx)}
                                        disabled={rows.length === 1}
                                    >
                                        Remove
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </Table>

                    <Row spacing="300">
                        <Button type="secondary" size="small" onClick={addRow}>Add row</Button>
                    </Row>

                    <Row spacing="300">
                        <Button type="primary" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save inputs'}
                        </Button>
                    </Row>
                </Column>
            )}
        </Column>
    );
};

export default DeliveryStationInputs;
